import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { formatAgo, formatSol, formatToken, formatUsd, shorten } from "../lib/format";
import type {
  BalanceMoney,
  BalanceRead,
  BalanceReservation,
  UnpricedBalanceRow,
  WalletBalanceRow,
} from "../types";
import type { BalanceSplit, SpotView } from "@auto-pools/core";

const EDGE = 8;
/** The five row colors of the stacked bar; the rent row uses --faint, so it never consumes one. */
const BAR_COLORS = 5;
/** A share below this width would disappear; the bar keeps every priced row visible. */
const MIN_SEGMENT = "3px";

type Mode = "usd" | "sol";

const MODES: { id: Mode; label: string }[] = [
  { id: "usd", label: "USD" },
  { id: "sol", label: "SOL" },
];

interface DisplayRow {
  key: string;
  color: string;
  label: ReactNode;
  value: BalanceMoney;
  sharePercent: string | null;
  positionId: string | null;
}

/** A missing matchMedia reports no match instead of throwing. */
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    // jsdom and embedded views have no matchMedia; the cast keeps that possibility visible.
    const matchMedia = window.matchMedia as typeof window.matchMedia | undefined;
    if (!matchMedia) return;
    const media = matchMedia.call(window, query);
    setMatches(media.matches);
    const update = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };
    media.addEventListener("change", update);
    return () => {
      media.removeEventListener("change", update);
    };
  }, [query]);
  return matches;
}

/** Server decimal strings are only formatted here: no sums, no conversions, no substituted zeros. */
function moneyText(value: BalanceMoney, mode: Mode) {
  if (mode === "sol") return formatSol(value.sol);
  const formatted = formatUsd(value.usd);
  return formatted === "Unavailable" ? "—" : formatted;
}

function tokenAmount(amount: string | null) {
  const formatted = formatToken(amount, "long");
  return formatted === "Unavailable" ? "—" : formatted;
}

function ageLabel(observedAt: string) {
  const ago = formatAgo(observedAt);
  return ago.endsWith(" ago") ? `${ago.slice(0, -" ago".length)} old` : ago;
}

function clockLabel(observedAt: string) {
  const at = new Date(observedAt);
  if (!Number.isFinite(at.getTime())) return "an unknown time";
  return `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
}

function symbolText(row: Pick<WalletBalanceRow, "mint" | "symbol" | "symbolMissing">) {
  return row.symbolMissing ? shorten(row.mint) : row.symbol;
}

function usesScaledUiAmount(row: WalletBalanceRow | UnpricedBalanceRow) {
  return "usesScaledUiAmount" in row && row.usesScaledUiAmount === true;
}

function SymbolName({
  row,
}: {
  row: Pick<WalletBalanceRow, "mint" | "symbol" | "symbolMissing" | "symbolVerified">;
}) {
  const text = symbolText(row);
  if (row.symbolVerified) return <>{text}</>;
  return (
    <span className="unverified" title={row.mint}>
      {text}
    </span>
  );
}

function WalletLabel({
  row,
  suffix,
  fees = false,
}: {
  row: WalletBalanceRow | UnpricedBalanceRow;
  suffix?: string;
  /** Native SOL is the fee budget (design 58): it is free cash, not part of the invested amount. */
  fees?: boolean;
}) {
  return (
    <>
      {"Wallet · "}
      <span className="num">{tokenAmount(row.amount)}</span> <SymbolName row={row} />
      {usesScaledUiAmount(row) && (
        <span className="scaled-pool-units-compact"> · pool units (unscaled)</span>
      )}
      {fees && <span className="balance-fees"> · fees</span>}
      {suffix}
    </>
  );
}

function ReservationNote({ reservation }: { reservation: BalanceReservation }) {
  return (
    <small className="balance-reservation">
      <span className="num">{tokenAmount(reservation.amount)}</span> {reservation.symbol}
      {reservation.pair === null
        ? " reserved by an operation in progress"
        : ` reserved for ${reservation.pair} opening`}
    </small>
  );
}

/** Tooltip placement from rendered geometry; the server's share strings are never added up. */
function tooltipAnchor(segment: HTMLElement, track: RefObject<HTMLDivElement | null>) {
  const bounds = track.current?.getBoundingClientRect();
  if (!bounds) return { left: 0, alignRight: false };
  const left = segment.getBoundingClientRect().left - bounds.left;
  return { left, alignRight: bounds.width > 0 && left > bounds.width * 0.6 };
}

export interface BalanceSummaryProps {
  data: BalanceRead | null;
  error: Error | null;
  loading: boolean;
  positionsError: boolean;
  onCheckAgain: () => void;
  onOpenSettings?: () => void;
  onShowPosition: (positionId: string) => void;
  /**
   * The Bybit subaccount (design 55/58): a second, separate account section in the popover, never
   * folded into the Solana wallet's stacked bar — the bar's shares are always server-computed for
   * the Solana rows it carries. Its figures come from the server (`BalanceRead.bybit`); this prop
   * adds the lifecycle (whether the section exists) and the coin count.
   */
  spot?: Pick<SpotView, "lifecycle" | "coins"> | null;
  /** The cell label over the figure (design 58: `Total · invested $X`). */
  label?: ReactNode;
  /** Drawn right after the figure and its chevron: the all-time result of both engines. */
  result?: ReactNode;
}

/**
 * The Total cell of the positions summary row and its popover (bottom sheet below 760px). Every
 * number is a server string; an unavailable part shows "—" with the server's reason, never $0.00.
 * The figure is `split.total.usd` only: while any part of it is unknown it is not drawn, so an
 * unread Bybit subaccount never shows a smaller Solana-only Total.
 */
export function BalanceSummary({
  data,
  error,
  loading,
  positionsError,
  onCheckAgain,
  onOpenSettings,
  onShowPosition,
  spot,
  label = "Total",
  result,
}: BalanceSummaryProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("usd");
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  // Tooltip geometry comes from the rendered segment; shares are never added up in the browser.
  const [tooltip, setTooltip] = useState<{ key: string; left: number; alignRight: boolean } | null>(
    null,
  );
  const [armedKey, setArmedKey] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState({ left: EDGE, top: EDGE });
  const trigger = useRef<HTMLButtonElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const modeSwitch = useRef<HTMLButtonElement>(null);
  const dialogId = useId();
  const titleId = useId();
  const sheet = useMediaQuery("(max-width: 760px)");
  const touchMode = useMediaQuery("(hover: none)");

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    setHoverKey(null);
    setTooltip(null);
    // A two-tap arming never survives a close: the next tap must arm again.
    setArmedKey(null);
    if (restoreFocus) trigger.current?.focus();
  }, []);
  const place = useCallback(() => {
    const anchor = trigger.current;
    const panel = popup.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const left = Math.min(Math.max(EDGE, a.left), Math.max(EDGE, innerWidth - p.width - EDGE));
    const below = a.bottom + 6;
    const top =
      below + p.height <= innerHeight - EDGE ? below : Math.max(EDGE, a.top - p.height - 6);
    setPopupPosition({ left, top });
  }, []);

  useLayoutEffect(() => {
    if (open && !sheet) place();
  }, [open, place, sheet]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      // Every close path returns the caret to the pill, including a backdrop or background tap.
      if (!popup.current?.contains(target) && !trigger.current?.contains(target)) close(true);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(true);
    };
    window.addEventListener("pointerdown", outside);
    window.addEventListener("keydown", key);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("pointerdown", outside);
      window.removeEventListener("keydown", key);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [close, open, place]);
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      modeSwitch.current?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [open]);

  const summary = data?.balance ?? null;
  const available = summary !== null && summary.status === "available";
  const noWallet = summary?.status === "unavailable" && summary.reason === "no wallet configured";
  const skeleton = loading && data === null && error === null;
  // A response without the refresh outcome is treated as no outcome, never as a successful one.
  const lastRefresh = data?.lastRefresh ?? null;
  const refreshFailed = lastRefresh !== null && !lastRefresh.ok;
  // Any remembered summary can be stale, including an unavailable one: the latest rejection is
  // shown next to whatever is displayed, never swallowed.
  const stale = summary !== null && (positionsError || error !== null || refreshFailed);
  const refreshReason = lastRefresh !== null && !lastRefresh.ok ? lastRefresh.reason : null;

  const rows: DisplayRow[] = [];
  if (summary && available) {
    let index = 0;
    const nextColor = () => `var(--bal-${String((index++ % BAR_COLORS) + 1)})`;
    for (const row of summary.positionRows)
      rows.push({
        key: row.key,
        color: nextColor(),
        label: `${row.pair} · LP + fees`,
        value: row.value,
        sharePercent: row.sharePercent,
        positionId: row.positionId,
      });
    if (summary.rent)
      rows.push({
        key: summary.rent.key,
        color: "var(--faint)",
        label: "Rent deposits · returned on close",
        value: summary.rent.value,
        sharePercent: summary.rent.sharePercent,
        positionId: null,
      });
    for (const row of summary.walletRows)
      rows.push({
        key: row.key,
        color: nextColor(),
        label: <WalletLabel row={row} fees={row.program === "system"} />,
        value: row.value,
        sharePercent: row.sharePercent,
        positionId: null,
      });
  }
  const segments = rows.filter((row) => row.sharePercent !== null);
  // The server counts the Bybit subaccount into Total; the bar's shares stay of the Solana side.
  const bybit = data?.bybit ?? null;
  const bybitUnavailable = bybit !== null && bybit.value.usd === null;
  // The only Total drawn (row and footer): the split's, never `bybit.total` or the Solana total,
  // which would read as a smaller Total while the subaccount is unread.
  const split: BalanceSplit | null = data?.split ?? null;
  const total: BalanceMoney = split?.total ?? { usd: null, sol: null };
  const shareOf = bybit ? "of the Solana side" : "of balance";
  const tooltipRow = segments.find((row) => row.key === tooltip?.key) ?? null;

  const reservationsFor = (rowKey: string) =>
    (summary?.reserved ?? [])
      .filter((reservation) => reservation.rowKey === rowKey)
      .map((reservation) => (
        <ReservationNote key={reservation.operationId} reservation={reservation} />
      ));

  const navigate = (positionId: string) => {
    close();
    onShowPosition(positionId);
  };
  const activate = (key: string, positionId: string) => {
    // On touch a tap focuses the row first, so the first tap only arms it; a second tap navigates.
    if (touchMode && armedKey !== key) {
      setArmedKey(key);
      return;
    }
    navigate(positionId);
  };

  const markers: string[] = [];
  if (summary) {
    if (summary.unpriced.length) markers.push(`· ${String(summary.unpriced.length)} unpriced`);
    if (summary.excluded.length) markers.push(`· ${String(summary.excluded.length)} unavailable`);
    if (summary.totals.total.reason === "SOL mark unavailable")
      markers.push("· prices unavailable");
    if (stale) markers.push(`· ${ageLabel(summary.observedAt)}`);
  }
  if (bybitUnavailable) markers.push("· Bybit unavailable");
  const markerSpans = markers.map((marker) => (
    <span key={marker} className="balance-marker">
      {marker}
    </span>
  ));

  if (noWallet)
    return (
      <span className="balance-no-wallet-cell">
        <span className="summary-label">{label}</span>{" "}
        <span className="balance-pill balance-no-wallet">
          No wallet ·{" "}
          <button className="link" onClick={onOpenSettings}>
            Import in Settings
          </button>
        </span>
      </span>
    );

  // Both figures come from the same server read as Total, so the section and the sum agree.
  const unread: BalanceMoney = { usd: null, sol: null };
  const hasBybitAccount = bybit !== null || (!!spot && spot.lifecycle !== "off");
  const coinCount = spot ? spot.coins.length : null;
  const bybitAccount = hasBybitAccount && (
    <div className="balance-spot-account">
      <strong>Bybit subaccount</strong>
      <div className="balance-spot-row">
        <span>{coinCount === null ? "Coins" : `Coins · ${String(coinCount)}`}</span>
        <span className="num">{moneyText(bybit?.coins ?? unread, mode)}</span>
      </div>
      <div className="balance-spot-row">
        <span>Free USDT</span>
        <span className="num">{moneyText(bybit?.freeUsdt ?? unread, mode)}</span>
      </div>
    </div>
  );
  const note = [
    summary?.note,
    hasBybitAccount ? "Money moves between the two accounts only by hand." : null,
    "SOL for fees is not part of the invested amount.",
  ]
    .filter((part): part is string => !!part)
    .join(" ");
  const chevron = (
    <svg className="balance-chevron" viewBox="0 0 14 14" aria-hidden="true">
      <path d="m3.5 5.5 3.5 3.5 3.5-3.5" />
    </svg>
  );
  const checkAgain = (
    <button className="link balance-check-again" onClick={onCheckAgain}>
      Check again
    </button>
  );

  return (
    <>
      <button
        ref={trigger}
        className={`balance-pill ${stale ? "stale" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dialogId}
        disabled={skeleton}
        onClick={() => {
          if (open) close(true);
          else setOpen(true);
        }}
      >
        <span className="summary-label">{label}</span>{" "}
        <span className="summary-figure">
          {skeleton ? (
            <span className="balance-skeleton wide" />
          ) : !summary || !available ? (
            <>
              <span className="neg">Balance unavailable</span>
              {markerSpans}
              {chevron}
            </>
          ) : (
            <>
              <span className="num summary-value balance-pill-total">
                {moneyText(total, "usd")}
              </span>
              {chevron} {result}
              {markerSpans}
            </>
          )}
        </span>
      </button>
      {open &&
        createPortal(
          <>
            {sheet && <div className="balance-backdrop" role="presentation" />}
            <div
              ref={popup}
              id={dialogId}
              className={`balance-popover ${sheet ? "balance-sheet" : ""}`}
              role="dialog"
              aria-modal="false"
              aria-labelledby={titleId}
              style={sheet ? undefined : popupPosition}
            >
              <div className="balance-popover-heading">
                <strong id={titleId}>Balance</strong>
                <div className="segmented balance-mode" role="group" aria-label="Balance unit">
                  {MODES.map((item, position) => (
                    <button
                      key={item.id}
                      ref={position === 0 ? modeSwitch : undefined}
                      className={mode === item.id ? "active" : ""}
                      aria-pressed={mode === item.id}
                      onClick={() => {
                        setMode(item.id);
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                {sheet && (
                  <button
                    className="icon-button balance-sheet-close"
                    aria-label="Close"
                    onClick={() => {
                      close(true);
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              {summary && stale && (
                <p className="balance-stale">
                  <span className="warn">
                    as of {clockLabel(summary.observedAt)} · refreshing failed
                  </span>
                  {refreshReason !== null && (
                    <span className="balance-stale-reason">{refreshReason}</span>
                  )}
                  {checkAgain}
                </p>
              )}
              {!summary || !available ? (
                <>
                  <p className="balance-unavailable">
                    Wallet balances could not be read. Nothing is shown as zero.
                  </p>
                  {summary?.reason !== undefined && summary.reason !== refreshReason && (
                    <p className="balance-stale-reason">{summary.reason}</p>
                  )}
                  {/* The stale line already carries the retry when a refresh failed. */}
                  {!stale && <div className="balance-footer">{checkAgain}</div>}
                </>
              ) : (
                <>
                  <div className="balance-bar-shell">
                    <div className="balance-bar" ref={bar}>
                      {segments.map((row) => (
                        <span
                          key={row.key}
                          data-row={row.key}
                          className={`balance-segment ${hoverKey !== null && hoverKey !== row.key ? "dim" : ""}`}
                          // The share drives flex-grow only: the CSS engine distributes the width,
                          // so a trailing segment can never be pushed out of the bar.
                          style={{
                            flexGrow: row.sharePercent ?? undefined,
                            flexBasis: MIN_SEGMENT,
                            background: row.color,
                          }}
                          onPointerEnter={(event) => {
                            setHoverKey(row.key);
                            setTooltip({
                              key: row.key,
                              ...tooltipAnchor(event.currentTarget, bar),
                            });
                          }}
                          onPointerLeave={() => {
                            setHoverKey(null);
                            setTooltip(null);
                          }}
                        />
                      ))}
                    </div>
                    {tooltipRow && tooltip && (
                      <span
                        className={`balance-tooltip ${tooltip.alignRight ? "align-right" : ""}`}
                        style={{ left: `${String(tooltip.left)}px` }}
                      >
                        {tooltipRow.label}
                        {" · "}
                        <span className="num">{moneyText(tooltipRow.value, mode)}</span>
                        {" · "}
                        <span className="num">{`${String(tooltipRow.sharePercent)}%`}</span>
                        {` ${shareOf}`}
                      </span>
                    )}
                  </div>
                  <span className="balance-account-heading">Solana wallet</span>
                  <ul className="balance-rows" role="list">
                    {rows.map(({ key, color, label, value, sharePercent, positionId }) => (
                      <li key={key}>
                        <div
                          className={`balance-row ${hoverKey === key ? "hover" : ""} ${
                            armedKey === key ? "active" : ""
                          } ${hoverKey !== null && hoverKey !== key ? "dim" : ""}`}
                          style={{ "--row-color": color } as CSSProperties}
                          tabIndex={0}
                          role={positionId === null ? undefined : "button"}
                          onPointerEnter={() => {
                            setHoverKey(key);
                          }}
                          onPointerLeave={() => {
                            setHoverKey(null);
                          }}
                          onFocus={() => {
                            setHoverKey(key);
                          }}
                          onBlur={() => {
                            setHoverKey(null);
                          }}
                          onClick={
                            positionId === null
                              ? undefined
                              : () => {
                                  activate(key, positionId);
                                }
                          }
                          onKeyDown={
                            positionId === null
                              ? undefined
                              : (event) => {
                                  if (event.key !== "Enter" && event.key !== " ") return;
                                  event.preventDefault();
                                  navigate(positionId);
                                }
                          }
                        >
                          <span
                            className="balance-dot"
                            style={{ background: color }}
                            aria-hidden="true"
                          />
                          <span className="balance-row-label">{label}</span>
                          {sharePercent !== null && (
                            <span className="sr-only">{`${sharePercent}% ${shareOf}`}</span>
                          )}
                          {positionId !== null && <span className="row-link">Show position →</span>}
                          <span className="num balance-row-value">{moneyText(value, mode)}</span>
                        </div>
                        {reservationsFor(key)}
                      </li>
                    ))}
                    {summary.unpriced.map((row) => (
                      <li key={`unpriced:${row.key}`}>
                        <div className="balance-row unpriced" tabIndex={0}>
                          <span className="balance-dot empty" aria-hidden="true" />
                          <span className="balance-row-label">
                            <WalletLabel row={row} suffix={` · ${row.reason}`} />
                          </span>
                          <span className="num balance-row-value">—</span>
                        </div>
                        {/* An unpriced wallet row still holds the reserved funds it names. */}
                        {reservationsFor(row.key)}
                      </li>
                    ))}
                    {summary.excluded.map((row) => (
                      <li key={`excluded:${row.key}`}>
                        <div className="balance-row unpriced" tabIndex={0}>
                          <span className="balance-dot empty" aria-hidden="true" />
                          <span className="balance-row-label">{row.label}</span>
                          <span className="num balance-row-value">{`— · ${row.reason}`}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {bybitAccount}
                  <div className="balance-footer">
                    <span className="balance-footer-label">Total</span>
                    {moneyText(total, mode === "usd" ? "sol" : "usd") !== "—" && (
                      <span className="num balance-footer-alt">
                        {`≈ ${moneyText(total, mode === "usd" ? "sol" : "usd")}`}
                      </span>
                    )}
                    <span className="num balance-footer-total">{moneyText(total, mode)}</span>
                    {summary.excluded.length > 0 && (
                      <span className="balance-marker">{`· ${String(summary.excluded.length)} unavailable`}</span>
                    )}
                    {bybitUnavailable && (
                      <span className="balance-marker">· Bybit unavailable</span>
                    )}
                  </div>
                  {summary.reserved === null && summary.reservedReason !== undefined && (
                    <small className="balance-note">
                      {`Reserved amounts unavailable · ${summary.reservedReason}`}
                    </small>
                  )}
                  <small className="balance-note">{note}</small>
                </>
              )}
              {!(summary && available) && bybitAccount}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
